
import React, { useState } from 'react';
import { Box, Button, Container, TextField, Typography, Link, Paper, Avatar, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { login } from '../../api';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const data = await login(email, password);
      // Save token, redirect, etc.
      localStorage.setItem('token', data.token);
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => navigate('/home'), 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box component="header" sx={{ p: 3, bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: '#E94057', width: 40, height: 40 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.5 2 5.5 4.5 5.5 8c0 1.5.5 2.9 1.4 4l-2.9 7h3l1-2.5h6l1 2.5h3l-2.9-7c.9-1.1 1.4-2.5 1.4-4 0-3.5-3-6-6.5-6zm-3 12.5l1-2.5h4l1 2.5H9zm3-5.5c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3z"/>
          </svg>
        </Avatar>
        <Typography variant="h6" fontWeight={700} color="#E94057">BabyOnTheGo</Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
        {/* Hero Image */}
        <Box sx={{ width: '100%', maxWidth: 900, height: 300, borderRadius: 2, overflow: 'hidden', mb: -7, position: 'relative', zIndex: 1 }}>
          <img
            src="https://images.unsplash.com/photo-1584839404042-8bc02dd17535?w=1200&h=400&fit=crop&crop=center"
            alt="Baby diaper changing supplies"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>

        {/* Login Card */}
        <Container maxWidth="xs" sx={{ zIndex: 2, mt: 0 }}>
          <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography component="h1" variant="h5" fontWeight={600} mb={2}>
                Welcome Back
              </Typography>
              {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>}
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, background: 'linear-gradient(135deg, #E94057 0%, #F27121 100%)' }}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Logging In...' : 'Log In'}
                </Button>
                <Box display="flex" flexDirection="column" alignItems="center" gap={1} mt={2}>
                  <Link href="/signup" variant="body2" onClick={e => { e.preventDefault(); navigate('/signup'); }} sx={{ fontWeight: 600, color: '#E94057' }}>
                    Sign Up
                  </Link>
                  <Link href="/forgot-password" variant="body2" onClick={e => { e.preventDefault(); navigate('/forgot-password'); }} sx={{ color: '#E94057' }}>
                    Forgot Password?
                  </Link>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
